import React, { useMemo, useState } from "react";
import { Check, X, Loader2, Filter, Wallet, RefreshCw } from "lucide-react";
import { fmtMoney as _fmtMoneyShared } from "@/lib/format";

const PERIODS = [
  { v: "all", label: "Alles" },
  { v: "7d", label: "7 giorni" },
  { v: "30d", label: "30 giorni" },
  { v: "month", label: "Diesen Monat" },
  { v: "3m", label: "3 mesi" },
  { v: "year", label: "Aktuelles Jahr" },
];

// Status cycle: unpaid (red) → paid (green) → carried_over (orange) → unpaid
// Semantica (richiesta Wertia):
//  - unpaid       (rosso)    = ancora non pagato
//  - paid         (verde)    = pagato entro fine mese
//  - carried_over (arancione) = riportato al mese successivo
const NEXT_STATUS = { unpaid: "paid", paid: "carried_over", carried_over: "unpaid", processing: "paid" };

const STATUS_META = {
  unpaid: {
    label: "Nein",
    icon: X,
    cls: "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 hover:bg-rose-200/70",
  },
  carried_over: {
    label: "Verschoben",
    icon: Loader2,
    cls: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200/70",
  },
  // legacy "processing" → same look as carried_over
  processing: {
    label: "In Bearbeitung",
    icon: Loader2,
    cls: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200/70",
  },
  paid: {
    label: "Ja",
    icon: Check,
    cls: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200/70",
  },
};

/** Local wrapper to preserve the existing "—" placeholder for null/empty values
 *  while delegating numeric formatting to the shared Swiss accounting helper
 *  (1'234,56) defined in /app/frontend/src/lib/format.js. */
function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  return _fmtMoneyShared(n);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH");
  } catch { return "—"; }
}

function inPeriod(iso, period) {
  if (!iso) return false;
  if (period === "all") return true;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const days = (now - d) / 86400000;
  if (period === "7d") return days <= 7 && days >= 0;
  if (period === "30d") return days <= 30 && days >= 0;
  if (period === "3m") return days <= 90 && days >= 0;
  if (period === "month") {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }
  if (period === "year") return d.getFullYear() === now.getFullYear();
  return true;
}

function deriveStatus(p) {
  if (p.commission_status) return p.commission_status;
  return p.commission_paid ? "paid" : "unpaid";
}

export default function CommissionsTable({ policies = [], commissionPreviews = {}, userRole = "basic", userFinma = "", onChangeStatus, onRefresh }) {
  const [period, setPeriod] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshingId, setRefreshingId] = useState(null);

  const handleRefresh = async (policyId) => {
    if (!onRefresh || refreshingId) return;
    setRefreshingId(policyId);
    try {
      await onRefresh(policyId);
    } finally {
      setRefreshingId(null);
    }
  };

  const canToggle = userRole === "master" || userRole === "admin" || userRole === "control";
  // Basic users see CHF amount they will receive but NOT the percentage —
  // company margins remain confidential.
  const hidePercent = userRole === "basic";

  const rows = useMemo(() => {
    return policies
      .map((p) => {
        const cp = commissionPreviews[p.id] || {};
        return {
          id: p.id,
          company: p.company || "—",
          policy_number: p.policy_number || "—",
          insurance_type: p.insurance_type,
          start_date: p.start_date,
          end_date: p.end_date,
          premium: Number(p.premium || 0),
          currency: p.currency || cp.currency || "CHF",
          pct: cp.pct || 0,
          gross: cp.gross || 0,
          status: deriveStatus(p),
          status_at: p.commission_status_at || p.commission_paid_at || null,
        };
      })
      .filter((r) => {
        if (period !== "all" && !inPeriod(r.start_date, period)) return false;
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        return true;
      });
  }, [policies, commissionPreviews, period, statusFilter]);

  const totals = useMemo(() => {
    const totalGross = rows.reduce((acc, r) => acc + (r.gross || 0), 0);
    const paid = rows.reduce((acc, r) => acc + (r.status === "paid" ? r.gross || 0 : 0), 0);
    const carried = rows.reduce((acc, r) => acc + ((r.status === "carried_over" || r.status === "processing") ? r.gross || 0 : 0), 0);
    const unpaid = totalGross - paid - carried;
    return { count: rows.length, totalGross, paid, processing: carried, unpaid };
  }, [rows]);

  return (
    <div className="p-3 sm:p-4 space-y-4" data-testid="commissions-table-wrap">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="label-uppercase">Zeitraum</span>
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              className={`px-3 py-1.5 text-xs transition-colors ${period === p.v ? "bg-prime text-white" : "bg-transparent hover:bg-secondary"}`}
              data-testid={`commission-period-${p.v}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="text-muted-foreground/40 px-1">·</span>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {[
            { v: "all", label: "Alle" },
            { v: "paid", label: "Bezahlt" },
            { v: "carried_over", label: "Verschoben" },
            { v: "unpaid", label: "Zu bezahlen" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`px-3 py-1.5 text-xs transition-colors ${statusFilter === s.v ? "bg-prime text-white" : "bg-transparent hover:bg-secondary"}`}
              data-testid={`commission-status-${s.v}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="border border-border rounded p-3" data-testid="comm-card-count">
          <div className="label-uppercase text-muted-foreground">Policen</div>
          <div className="font-display font-bold text-lg mt-1">{totals.count}</div>
        </div>
        <div className="border border-border rounded p-3" data-testid="comm-card-total">
          <div className="label-uppercase text-muted-foreground">Gesamt</div>
          <div className="font-display font-bold text-lg mt-1">CHF {fmtMoney(totals.totalGross)}</div>
        </div>
        <div className="border border-emerald-200 dark:border-emerald-900/40 rounded p-3 bg-emerald-50/40 dark:bg-emerald-900/10" data-testid="comm-card-paid">
          <div className="label-uppercase text-emerald-700 dark:text-emerald-400">Bezahlt</div>
          <div className="font-display font-bold text-lg mt-1 text-emerald-700 dark:text-emerald-400">CHF {fmtMoney(totals.paid)}</div>
        </div>
        <div className="border border-amber-300 dark:border-amber-900/40 rounded p-3 bg-amber-50/40 dark:bg-amber-900/10" data-testid="comm-card-processing">
          <div className="label-uppercase text-amber-700 dark:text-amber-400">Verschoben</div>
          <div className="font-display font-bold text-lg mt-1 text-amber-700 dark:text-amber-400">CHF {fmtMoney(totals.processing)}</div>
        </div>
        <div className="border border-prime/30 rounded p-3 bg-prime/5" data-testid="comm-card-unpaid">
          <div className="label-uppercase text-prime">Zu bezahlen</div>
          <div className="font-display font-bold text-lg mt-1 text-prime">CHF {fmtMoney(totals.unpaid)}</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded overflow-hidden">
        <div className={`grid ${hidePercent ? "grid-cols-[1.4fr_1fr_0.7fr_0.8fr_0.9fr]" : "grid-cols-[1.4fr_1fr_0.6fr_0.7fr_0.8fr_0.9fr]"} gap-2 px-3 py-2 bg-secondary text-[10px] uppercase font-bold tracking-wider text-muted-foreground`}>
          <div>Gesellschaft</div>
          <div>Policennummer</div>
          {!hidePercent && <div className="text-right">%</div>}
          <div className="text-right">Betrag</div>
          <div className="text-right">Beginn</div>
          <div className="text-center">Bezahlt</div>
        </div>
        {rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-muted-foreground">Keine Provisionen im gewählten Zeitraum</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.unpaid;
              const Icon = meta.icon;
              return (
                <div
                  key={r.id}
                  className={`grid ${hidePercent ? "grid-cols-[1.4fr_1fr_0.7fr_0.8fr_0.9fr]" : "grid-cols-[1.4fr_1fr_0.6fr_0.7fr_0.8fr_0.9fr]"} gap-2 px-3 py-2.5 text-sm items-center hover:bg-secondary/40`}
                  data-testid={`commission-row-${r.id}`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-amber-600 shrink-0" strokeWidth={1.7} />
                    <span className="font-medium truncate">{r.company}</span>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground truncate">{r.policy_number}</div>
                  {!hidePercent && (
                    <div className="text-right text-xs font-bold text-amber-700 dark:text-amber-400">
                      {r.pct ? `${r.pct}%` : "—"}
                    </div>
                  )}
                  <div className="text-right text-xs font-mono flex items-center justify-end gap-1.5">
                    <span>{`${r.currency} ${fmtMoney(r.gross)}`}</span>
                    {onRefresh && (
                      <button
                        type="button"
                        onClick={() => handleRefresh(r.id)}
                        disabled={!!refreshingId}
                        title="Provision aktualisieren — Berechnung neu ausführen"
                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full border transition-colors shrink-0 ${
                          !r.gross
                            ? "border-amber-400 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                            : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                        } ${refreshingId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        data-testid={`commission-refresh-${r.id}`}
                      >
                        <RefreshCw className={`w-3 h-3 ${refreshingId === r.id ? "animate-spin" : ""}`} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                  <div className="text-right text-xs font-mono text-muted-foreground">{fmtDate(r.start_date)}</div>
                  <div className="flex justify-center">
                    <button
                      type="button"
                      disabled={!canToggle || !r.gross}
                      onClick={() => {
                        if (!canToggle || !r.gross) return;
                        const next = NEXT_STATUS[r.status] || "unpaid";
                        onChangeStatus?.(r.id, next);
                      }}
                      title={
                        r.status === "paid"
                          ? `Bezahlt am ${fmtDate(r.status_at)} — Klick zum Verschieben auf nächsten Monat`
                          : r.status === "carried_over" || r.status === "processing"
                          ? `Auf nächsten Monat verschoben (${fmtDate(r.status_at)}) — Klick zum Markieren als unbezahlt`
                          : "Klicken um als 'Bezahlt' zu markieren"
                      }
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${meta.cls} ${(!canToggle || !r.gross) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`commission-toggle-${r.id}`}
                    >
                      <Icon className={`w-3 h-3 ${(r.status === "processing" || r.status === "carried_over") ? "animate-spin" : ""}`} strokeWidth={3} />
                      {meta.label}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
